import React, { useState, useEffect } from 'react';
import { ArrowLeft, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import errorSound from '../assets/sounds/error2.mp3';
import successSound from '../assets/sounds/success.wav';

interface PrimeSortingGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

const PrimeSortingGame: React.FC<PrimeSortingGameProps> = ({ mode, onReset, language }) => {
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [correctlySortedNumbers, setCorrectlySortedNumbers] = useState<number[]>([]);
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [gameComplete, setGameComplete] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const [playError] = useSound(errorSound, { volume: 0.3 });
  const [playSuccess] = useSound(successSound, { volume: 0.5 });

  const isPrime = (num: number): boolean => {
    if (num <= 1) return false;
    if (num <= 3) return true;
    if (num % 2 === 0 || num % 3 === 0) return false;
    
    for (let i = 5; i * i <= num; i += 6) {
      if (num % i === 0 || num % (i + 2) === 0) return false;
    }
    return true;
  };

  const generateNextNumber = () => {
    const maxNumber = level === 1 ? 20 : level === 2 ? 50 : 100;
    const num = Math.floor(Math.random() * (maxNumber - 2)) + 2;
    setCurrentNumber(num);
  };

  useEffect(() => {
    generateNextNumber();
  }, [level]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('text/plain', e.currentTarget.textContent || '');
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, isPrimeZone: boolean) => {
    e.preventDefault();
    const number = parseInt(e.dataTransfer.getData('text/plain'), 10);
    const isNumberPrime = isPrime(number);
    const correct = isNumberPrime === isPrimeZone;
    const nextCorrectAnswers = correctAnswers + (correct ? 1 : 0);

    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);

    if (correct) {
      setScore(score + 1);
      setCorrectAnswers(prev => prev + 1);
      setMessage({ 
        text: language === 'en' ? 'Correct!' : 'Correct !', 
        type: 'success' 
      });
    } else {
      playError();
      setMessage({ 
        text: language === 'en' 
          ? `Incorrect! ${number} is ${isNumberPrime ? 'prime' : 'not prime'}`
          : `Incorrect ! ${number} ${isNumberPrime ? 'est premier' : 'n\'est pas premier'}`,
        type: 'error' 
      });
    }

    if (correct) {
      setCorrectlySortedNumbers([...correctlySortedNumbers, number]);
    } 
    setCurrentNumber(null);

    if (true) {
      if (nextCorrectAnswers >= 10) {
        if (level < 3) {
          playSuccess();
          setShowLevelComplete(true);
          setTimeout(() => {
            setShowLevelComplete(false);
            setLevel(prev => prev + 1);
            setCorrectAnswers(0);
            setCorrectlySortedNumbers([]);
            setMessage(null);
            generateNextNumber();
          }, 2000);
        } else {
          setGameComplete(true);
          playSuccess();
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              await supabase.from('user_progress').insert({
                user_id: user.id,
                operation: 'prime',
                mode: 'sorting',
                questions_answered: newStats.questionsAnswered,
                correct_answers: newStats.correctAnswers,
                average_response_time: 0,
              });
            }
          } catch (error) {
            console.error('Error saving progress:', error);
          }
        }
      } else {
        setTimeout(() => {
          generateNextNumber();
        }, 500);
      }
    }
  };
    
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const resetGame = () => {
    setLevel(1);
    setScore(0);
    setGameComplete(false);
    setCorrectAnswers(0);
    setCorrectlySortedNumbers([]);
    setMessage(null);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
    generateNextNumber();
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        <div className="text-lg font-medium text-indigo-700 dark:text-indigo-300">
          {language === 'en' ? `Level ${level}` : `Niveau ${level}`}
        </div>
      </div>

      {showLevelComplete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-8 text-center shadow-xl
                          transform scale-95 opacity-0 animate-[fadeZoomIn_0.5s_ease-out_forwards]">
            <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {language === 'en' ? 'Level Complete!' : 'Niveau Terminé !'}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300">
              {language === 'en' 
                ? `Moving to Level ${level + 1}`
                : `Passage au Niveau ${level + 1}`}
            </p>
          </div>
        </div>
      )}
     
      {gameComplete ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Congratulations!' : 'Félicitations !'}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
            {language === 'en' 
              ? 'You\'ve completed all levels!'
              : 'Vous avez terminé tous les niveaux !'}
          </p>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
              hover:bg-indigo-700 dark:hover:bg-indigo-600 
              focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 text-center">
              {language === 'en' 
                ? 'Drag the numbers to the correct category'
                : 'Glissez les nombres dans la bonne catégorie'}
            </h2>
            <div className="flex justify-between items-center">
              
              <div className="text-slate-600 dark:text-slate-300 font-bold">
                {language === 'en' ? (
                  <>
                    <div>Correct answers required: {correctAnswers}/10</div>
                  </>
                ) : (
                  <>
                    <div>Réponses correctes demandées: {correctAnswers}/10</div>
                  </>
                )}
              </div>

              <div className="text-green-600 dark:text-green-400">
                {language === 'en'
                  ? `Score: ${stats.correctAnswers}`
                  : `Score : ${stats.correctAnswers}`}
              </div>
              
              <div className="text-red-600 dark:text-red-400">
                {language === 'en'
                  ? `Mistakes: ${stats.incorrectAnswers}`
                  : `Erreurs : ${stats.incorrectAnswers}`}
              </div>              

              <p className="text-slate-600 dark:text-slate-300 font-bold">
                {language === 'en'
                  ? `Accuracy: ${stats.questionsAnswered > 0
                    ? `${Math.round((stats.correctAnswers / stats.questionsAnswered) * 100)}%`
                    : '0%'}`
                  : `Précision : ${stats.questionsAnswered > 0
                    ? `${Math.round((stats.correctAnswers / stats.questionsAnswered) * 100)}%`
                    : '0%'}`}
              </p>

            </div>
          </div>

          <div className="flex justify-center mb-8 min-h-[4rem]">
            {currentNumber !== null ? (
              <div
                draggable
                onDragStart={handleDragStart}
                className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full text-2xl font-bold 
                  text-slate-800 dark:text-slate-100
                  hover:bg-indigo-100 dark:hover:bg-indigo-600 hover:text-indigo-600 dark:hover:text-indigo-100 
                  transition-colors flex items-center justify-center cursor-move shadow-md"
              >
                {currentNumber}
              </div>
            ) : (
              <div className="w-16 h-16" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div
              className="border-2 border-dashed border-emerald-200 dark:border-emerald-500/30 rounded-lg p-4 min-h-[200px]
                bg-emerald-50 dark:bg-emerald-900/30 flex flex-col items-center"
              onDrop={(e) => handleDrop(e, true)}
              onDragOver={handleDragOver}
            >
              <div className="font-semibold text-emerald-700 dark:text-emerald-300 mb-4">
                {language === 'en' ? 'Prime Numbers' : 'Nombres Premiers'}
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {correctlySortedNumbers
                  .filter(n => isPrime(n))
                  .map(n => (
                    <div key={n} className="w-16 h-16 bg-emerald-100 dark:bg-emerald-800 rounded-full 
                      flex items-center justify-center text-xl font-bold 
                      text-emerald-700 dark:text-emerald-300 shadow">
                      {n}
                    </div>
                  ))}
              </div>
            </div>

            <div
              className="border-2 border-dashed border-rose-200 dark:border-rose-500/30 rounded-lg p-4 min-h-[200px]
                bg-rose-50 dark:bg-rose-900/30 flex flex-col items-center"
              onDrop={(e) => handleDrop(e, false)}
              onDragOver={handleDragOver}
            >
              <div className="font-semibold text-rose-700 dark:text-rose-300 mb-4">
                {language === 'en' ? 'Not Prime' : 'Pas Premier'}
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {correctlySortedNumbers
                  .filter(n => !isPrime(n))
                  .map(n => (
                    <div key={n} className="w-16 h-16 bg-rose-100 dark:bg-rose-800 rounded-full 
                      flex items-center justify-center text-xl font-bold 
                      text-rose-700 dark:text-rose-300 shadow">
                      {n}
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {message && (
            <div className={`mt-4 flex items-center justify-center ${
              message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}>
              <span>{message.text}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PrimeSortingGame;